import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useCurrency } from '@/hooks/useCurrency';
import {
  DollarSign,
  CalendarDays,
  RefreshCw,
  FileText,
  Clock,
  Pencil,
  Trash2,
  CreditCard,
  Banknote,
  Tag,
  Bell,
} from 'lucide-react';
import type { Bill } from '@/lib/types';

interface BillDetailSheetProps {
  bill: Bill | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit: (bill: Bill) => void;
  onDelete: (bill: Bill) => void;
  onPay: (bill: Bill) => void;
}

const statusConfig: Record<string, { heroBg: string; badgeClass: string }> = {
  upcoming: {
    heroBg: 'bg-blue-50 dark:bg-blue-950/30',
    badgeClass: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  },
  paid: {
    heroBg: 'bg-green-50 dark:bg-green-950/30',
    badgeClass: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  },
  overdue: {
    heroBg: 'bg-red-50 dark:bg-red-950/30',
    badgeClass: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  },
  cancelled: {
    heroBg: 'bg-gray-50 dark:bg-gray-900/40',
    badgeClass: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  },
};

function DetailRow({ icon: Icon, label, children }: { icon: React.ElementType; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-muted/60">
        <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">{label}</p>
        <div className="text-sm text-foreground">{children}</div>
      </div>
    </div>
  );
}

export default function BillDetailSheet({ bill, open, onOpenChange, onEdit, onDelete, onPay }: BillDetailSheetProps) {
  const { fmt } = useCurrency();

  if (!bill) return null;

  const config = statusConfig[bill.status] ?? statusConfig.upcoming;
  const canPay = bill.status === 'upcoming' || bill.status === 'overdue';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[480px] h-[100dvh] flex flex-col p-0 gap-0 overflow-hidden pb-[env(safe-area-inset-bottom)]">
        {/* Hero */}
        <div className={`px-6 pt-8 pb-6 ${config.heroBg}`}>
          <SheetHeader className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/80 dark:bg-black/20 shadow-sm shrink-0 text-2xl leading-none">
                {bill.emoji || bill.name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <SheetTitle className="text-left leading-tight break-words">{bill.name}</SheetTitle>
                <SheetDescription className="sr-only">Bill details</SheetDescription>
                <span className={`mt-1 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${config.badgeClass}`}>
                  {bill.status}
                </span>
              </div>
            </div>

            <div>
              <span className="text-3xl font-bold tabular-nums">{fmt(bill.amount)}</span>
              {bill.daysUntilDue !== undefined && bill.status !== 'paid' && (
                <p className="text-sm text-muted-foreground mt-1">
                  {bill.daysUntilDue < 0
                    ? `${Math.abs(bill.daysUntilDue)} days overdue`
                    : bill.daysUntilDue === 0
                      ? 'Due today'
                      : `Due in ${bill.daysUntilDue} days`}
                </p>
              )}
            </div>
          </SheetHeader>
        </div>

        {/* Details */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <DetailRow icon={DollarSign} label="Amount">
            {fmt(bill.amount)}
            {bill.currency && <Badge variant="outline" className="ml-1.5 text-xs">{bill.currency}</Badge>}
          </DetailRow>

          <DetailRow icon={CalendarDays} label="Due Date">
            {new Date(bill.dueDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
          </DetailRow>

          {bill.isRecurring && bill.recurringPattern && (
            <DetailRow icon={RefreshCw} label="Recurring">
              Every {bill.recurringPattern.interval > 1 ? `${bill.recurringPattern.interval} ` : ''}
              {bill.recurringPattern.frequency}
            </DetailRow>
          )}

          {bill.category && (
            <DetailRow icon={Tag} label="Category">
              {typeof bill.category === 'string' ? bill.category : (bill.category as any)?.name}
            </DetailRow>
          )}

          {bill.account && (
            <DetailRow icon={Banknote} label="Account">
              {typeof bill.account === 'string' ? bill.account : (bill.account as any)?.name}
            </DetailRow>
          )}

          <DetailRow icon={Bell} label="Reminder">
            {bill.reminderDays} days before due
          </DetailRow>

          {bill.notes && (
            <DetailRow icon={FileText} label="Notes">
              <span className="whitespace-pre-wrap break-words">{bill.notes}</span>
            </DetailRow>
          )}

          {bill.isPaid && bill.paidDate && (
            <>
              <Separator />
              <DetailRow icon={CreditCard} label="Paid Date">
                {new Date(bill.paidDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
              </DetailRow>
              {bill.paidAmount != null && (
                <DetailRow icon={DollarSign} label="Paid Amount">
                  {fmt(bill.paidAmount)}
                </DetailRow>
              )}
            </>
          )}

          {bill.paymentHistory && bill.paymentHistory.length > 0 && (
            <>
              <Separator />
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Payment History</p>
              <div className="space-y-2">
                {bill.paymentHistory.map((entry, idx) => (
                  <div key={idx} className="flex items-center justify-between rounded-md border p-2 text-sm">
                    <span className="text-muted-foreground">
                      {new Date(entry.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                    <span className="font-semibold tabular-nums">{fmt(entry.amount)}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          <Separator />

          <DetailRow icon={Clock} label="Created">
            {new Date(bill.createdAt).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          </DetailRow>
        </div>

        {/* Footer actions */}
        <SheetFooter className="sticky bottom-0 border-t bg-background px-4 sm:px-6 py-3 gap-2 flex-row flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="min-h-[44px] mr-auto text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
            onClick={() => { onOpenChange(false); onDelete(bill); }}
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            Delete
          </Button>

          {canPay && (
            <Button
              variant="outline"
              size="sm"
              className="min-h-[44px]"
              onClick={() => { onOpenChange(false); onPay(bill); }}
            >
              <CreditCard className="h-4 w-4 mr-1.5" />
              Pay
            </Button>
          )}

          <Button
            size="sm"
            className="min-h-[44px]"
            onClick={() => { onOpenChange(false); onEdit(bill); }}
          >
            <Pencil className="h-4 w-4 mr-1.5" />
            Edit
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
